import React from 'react';
import { useTranslation } from 'react-i18next';
import { useMiningUpload } from '../../containers/MiningUploadPage/useMiningUpload';
import type { MiningUploadItem } from '../../containers/MiningUploadPage/types';
import {
  formatMiningUploadSize,
  miningUploadRoomOptions,
} from '../../containers/MiningUploadPage/presentation';
import V2AdminPageHeading from '../components/V2AdminPageHeading';
import { AdminTable } from '../components/V2AdminUI';
import V2Select from '../components/V2Select';
import '../admin/mining-upload.css';

const PAGE_SIZE = 50;

const V2AdminMiningUpload: React.FC = () => {
  const { t, i18n } = useTranslation('translation');
  const upload = useMiningUpload();
  const fileInput = React.useRef<HTMLInputElement>(null);
  const folderInput = React.useRef<HTMLInputElement | null>(null);
  const [page, setPage] = React.useState(1);
  const extension = upload.room === 6 ? '.xml' : '.txt';
  const pendingCount = upload.items.filter((item) => item.status === 'pending').length;
  const uploadedCount = upload.items.filter((item) => item.status === 'uploaded').length;
  const failedCount = upload.items.filter((item) => item.status === 'error').length;
  const currentPage = Math.min(page, Math.max(1, Math.ceil(upload.items.length / PAGE_SIZE)));

  const addSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    upload.addFiles(Array.from(event.currentTarget.files ?? []));
    event.currentTarget.value = '';
  };

  return (
    <>
      <V2AdminPageHeading title={t('admin_mining_upload')} />
      <section className="settings-panel v2-mining-upload">
        <div className="v2-mining-upload__controls">
          <div className="v2-mining-upload__room">
            <span>{t('admin_mining_upload_room')}</span>
            <V2Select
              value={upload.room}
              options={miningUploadRoomOptions}
              onChange={upload.setRoom}
              ariaLabel={t('admin_mining_upload_room')}
              disabled={upload.isUploading || upload.items.length > 0}
            />
          </div>
          <div className="v2-mining-upload__buttons">
            <button
              type="button"
              className="outline-button"
              disabled={upload.isUploading}
              onClick={() => fileInput.current?.click()}
            >
              {t('admin_mining_upload_add_files')}
            </button>
            <button
              type="button"
              className="outline-button"
              disabled={upload.isUploading}
              onClick={() => folderInput.current?.click()}
            >
              {t('admin_mining_upload_add_folder')}
            </button>
          </div>
        </div>
        <input
          ref={fileInput}
          type="file"
          multiple
          hidden
          accept={extension}
          disabled={upload.isUploading}
          onChange={addSelection}
        />
        <input
          ref={(element) => {
            folderInput.current = element;
            element?.setAttribute('webkitdirectory', '');
          }}
          type="file"
          multiple
          hidden
          disabled={upload.isUploading}
          onChange={addSelection}
        />
        <div className="v2-mining-upload__actions">
          <div className="v2-mining-upload__buttons">
            <button
              type="button"
              className="acid-button"
              disabled={upload.isUploading || pendingCount === 0}
              onClick={upload.startUpload}
            >
              {upload.isUploading
                ? t('admin_mining_upload_sending')
                : t('admin_mining_upload_start')}
            </button>
            <button
              type="button"
              className="outline-button"
              disabled={upload.isUploading || failedCount === 0}
              onClick={upload.retryErrors}
            >
              {t('admin_mining_upload_retry')}
            </button>
            <button
              type="button"
              className="outline-button"
              disabled={
                upload.isUploading || (upload.items.length === 0 && upload.skippedCount === 0)
              }
              onClick={() => {
                upload.clearQueue();
                setPage(1);
              }}
            >
              {t('admin_mining_upload_clear')}
            </button>
          </div>
          <p className="v2-mining-upload__summary" role="status" aria-live="polite">
            {t('admin_mining_upload_summary', {
              uploaded: uploadedCount,
              total: upload.items.length,
              failed: failedCount,
            })}
            {upload.skippedCount > 0
              ? ` · ${t('admin_mining_upload_skipped', { count: upload.skippedCount })}`
              : null}
          </p>
        </div>
        <AdminTable<MiningUploadItem>
          rows={upload.items.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)}
          rowKey="id"
          caption={t('admin_mining_upload')}
          minWidth={640}
          emptyText={t('admin_mining_upload_empty')}
          pagination={
            upload.items.length > PAGE_SIZE
              ? {
                  page: currentPage,
                  pageSize: PAGE_SIZE,
                  total: upload.items.length,
                  onChange: setPage,
                }
              : undefined
          }
          columns={[
            {
              key: 'file',
              title: t('admin_mining_upload_file'),
              render: (item) => (
                <div className="v2-mining-upload__file">
                  <strong>{item.name}</strong>
                  {item.relativePath !== item.name ? <small>{item.relativePath}</small> : null}
                </div>
              ),
            },
            {
              key: 'size',
              title: t('admin_mining_upload_size'),
              className: 'v2-mining-upload__size',
              render: (item) =>
                formatMiningUploadSize(item.size, i18n.resolvedLanguage ?? i18n.language),
            },
            {
              key: 'status',
              title: t('admin_mining_upload_status'),
              className: 'v2-mining-upload__status-cell',
              render: (item) => (
                <div
                  className={`v2-mining-upload__status v2-mining-upload__status--${item.status}`}
                >
                  <span>
                    {item.status === 'uploaded' ? <span aria-hidden="true">✓ </span> : null}
                    {t(`admin_mining_upload_status_${item.status}`)}
                  </span>
                  {item.error ? <small>{item.error}</small> : null}
                </div>
              ),
            },
          ]}
        />
      </section>
    </>
  );
};

export default V2AdminMiningUpload;
